import { useEffect, useRef, useState } from "react";
import { fmtIST } from "@/shared/lib/time";
import { useParams, Link } from "react-router-dom";
import api, { fmtErr, API } from "@/shared/lib/api";
import {
  ArrowLeft, Truck, Upload, FileText, CheckCircle2, Circle,
  IndianRupee, Image as ImageIcon, X, Receipt, Wallet, MessageCircle,
  AlertTriangle, Calendar, Download, Eye, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

/**
 * AccountsProjectBilling — for a single project, show:
 *   • DC folder grouped by BOQ line (delivered/pending/invoiced)
 *   • All DCs that haven't been billed yet
 *   • Action: select DCs + upload a Tally invoice PDF, link it to those DCs
 *   • All uploaded invoices for the project + their payment status
 */
export default function AccountsProjectBilling() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [selectedDCs, setSelectedDCs] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);   // outsourced lines billed without a DC
  const [showUpload, setShowUpload] = useState(false);
  const [showAdvance, setShowAdvance] = useState(false);
  const [recordPayFor, setRecordPayFor] = useState(null);   // invoice being paid
  const [followupFor, setFollowupFor] = useState(null);     // invoice for follow-up
  const [historyFor, setHistoryFor] = useState(null);       // invoice for follow-up history

  async function load() {
    try {
      const { data } = await api.get(`/projects/${code}/billing-summary`);
      setData(data);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Load failed");
    }
  }
  useEffect(() => { load(); }, [code]);

  if (!data) return <div className="p-8 text-zinc-500">Loading…</div>;
  const { project, summary, lines, challans, invoices } = data;
  const advanceInvoice = invoices.find((i) => (i.milestone || "").toLowerCase() === "advance");

  function toggleDC(cn) {
    setSelectedDCs((s) => s.includes(cn) ? s.filter((c) => c !== cn) : [...s, cn]);
  }
  function toggleLine(bid) {
    setSelectedLines((s) => s.includes(bid) ? s.filter((b) => b !== bid) : [...s, bid]);
  }
  const selectionCount = selectedDCs.length + selectedLines.length;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="accounts-project-page">
      {/* Header */}
      <Link to="/accounts/projects"
            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to billing dashboard
      </Link>

      <div className="rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-800 to-orange-950/60 text-white p-6 sm:p-9 mb-8 relative overflow-hidden shadow-xl">
        {/* Decorative element */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl -translate-y-20 translate-x-20 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-400/5 rounded-full blur-3xl translate-y-10 -translate-x-10 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300">
            <Receipt className="w-3 h-3" /> Accounts · Project Billing
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            {project.name}
          </h1>
          <div className="text-sm text-zinc-300 mt-2 flex flex-wrap gap-x-6 gap-y-1">
            {project.upr_no
              ? <span className="font-mono text-emerald-300 font-semibold" data-testid="apb-upr">UPR {project.upr_no}</span>
              : <span className="font-mono text-amber-300/80">UPR pending</span>}
            <span className="font-mono text-amber-300">{project.project_code}</span>
            <span>Client: <strong className="text-white">{project.client_name || "—"}</strong></span>
            {project.location && <span>Site: {project.location}</span>}
          </div>
          {/* Phase 34 r5 (Msg 760): team strip removed from Accounts —
              project-team display moved to the Project Detail page. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <Kpi label="Total DCs" value={summary.total_dcs} icon={<Truck className="w-3 h-3" />} />
            <Kpi label="Pending Billing" value={summary.pending_billing_dcs} tone="amber" icon={<Receipt className="w-3 h-3" />} />
            <Kpi label="Invoiced" value={`₹${(summary.invoice_total || 0).toLocaleString("en-IN")}`} tone="emerald" icon={<IndianRupee className="w-3 h-3" />} />
            <Kpi label="Balance Due" value={`₹${(summary.balance_total || 0).toLocaleString("en-IN")}`} tone="rose" icon={<Wallet className="w-3 h-3" />} />
          </div>
        </div>
      </div>

      {/* Advance Invoice card (Msg 877) — Accounts can raise a one-off
          advance invoice as soon as the project is active. Single card,
          single CTA, single status pill — no clutter. */}
      <AdvanceCard
        existing={advanceInvoice}
        onRaise={() => setShowAdvance(true)} />

      {/* Action bar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-sm text-zinc-600">
          {selectionCount > 0
            ? <span className="font-medium text-zinc-900">{selectedDCs.length > 0 && `${selectedDCs.length} DC${selectedDCs.length > 1 ? "s" : ""}`}{selectedDCs.length > 0 && selectedLines.length > 0 && " + "}{selectedLines.length > 0 && `${selectedLines.length} outsourced line${selectedLines.length > 1 ? "s" : ""}`} selected</span>
            : <>Tick the DC(s) — or outsourced line(s) — you&apos;ve billed in Tally, then upload the invoice PDF.</>}
        </div>
        <button
          disabled={selectionCount === 0}
          onClick={() => setShowUpload(true)}
          data-testid="upload-tally-btn"
          className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-all disabled:bg-zinc-300 disabled:cursor-not-allowed">
          <Upload className="w-4 h-4" /> Upload Tally Invoice
        </button>
      </div>

      {/* DC folder grouped by BOQ line */}
      <div className="space-y-4 mb-10" data-testid="dc-folder">
        {lines.length === 0 && (
          <div className="card-flat p-8 text-center text-zinc-400">No BOQ lines on this project yet.</div>
        )}
        {lines.map((l) => (
          <LineCard key={l.boq_id} line={l}
                    selectedDCs={selectedDCs}
                    onToggleDC={toggleDC}
                    selectedLines={selectedLines}
                    onToggleLine={toggleLine}
                    code={code} />
        ))}
      </div>

      {/* Uploaded Tally invoices */}
      {invoices.length > 0 && (
        <div className="mb-10">
          <h2 className="font-display text-xl font-bold tracking-tight mb-3">Uploaded Invoices</h2>
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm min-w-[920px]">
              <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-3">Invoice #</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-4 py-3">Received</th>
                  <th className="text-right px-4 py-3">Pending</th>
                  <th className="text-left px-4 py-3">Due</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Last Follow-up</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((i) => {
                  const total    = i.total || 0;
                  const received = i.amount_received || 0;
                  const balance  = i.balance_due ?? Math.max(total - received, 0);
                  return (
                    <tr key={i.id} className="border-t border-zinc-100"
                        data-testid={`uploaded-inv-${i.invoice_number}`}>
                      <td className="px-4 py-3 font-mono text-zinc-800">
                        {i.invoice_number}
                        {i.tally_pdf_url && (
                          <a href={i.tally_pdf_url} target="_blank" rel="noopener noreferrer"
                             className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline align-middle">
                            <FileText className="w-3 h-3" /> PDF
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">{i.invoice_date}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        ₹{total.toLocaleString("en-IN")}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-700">
                        ₹{received.toLocaleString("en-IN")}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${balance > 0 ? "text-rose-700" : "text-zinc-400"}`}>
                        ₹{balance.toLocaleString("en-IN")}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {i.due_date ? (
                          <span className={`inline-flex items-center gap-1 ${i.is_overdue ? "text-rose-700 font-medium" : "text-zinc-600"}`}>
                            {i.is_overdue && <AlertTriangle className="w-3 h-3" />}
                            <Calendar className="w-3 h-3" /> {i.due_date}
                          </span>
                        ) : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <PaymentPill status={i.payment_status} />
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {i.last_followup_at ? (
                          <button onClick={() => setHistoryFor(i)}
                                  data-testid={`followup-history-${i.invoice_number}`}
                                  className="inline-flex items-center gap-1 text-zinc-700 hover:text-orange-700">
                            <MessageCircle className="w-3.5 h-3.5" />
                            {(i.last_followup_at || "").slice(0, 10)}
                            {i.followup_count > 1 && (
                              <span className="ml-1 px-1.5 h-4 inline-flex items-center rounded-full bg-zinc-100 text-[10px]">{i.followup_count}</span>
                            )}
                          </button>
                        ) : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          {balance > 0 && i.payment_status !== "paid" && (
                            <button onClick={() => setRecordPayFor(i)}
                                    data-testid={`record-payment-${i.invoice_number}`}
                                    className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-emerald-600 text-white text-[11px] font-medium hover:bg-emerald-700">
                              <Wallet className="w-3 h-3" /> Record
                            </button>
                          )}
                          <button onClick={() => setFollowupFor(i)}
                                  data-testid={`add-followup-${i.invoice_number}`}
                                  className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full border border-zinc-300 text-[11px] font-medium hover:bg-zinc-50">
                            <MessageCircle className="w-3 h-3" /> Follow-up
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showUpload && (
        <UploadTallyModal
          projectCode={code}
          selectedDCs={selectedDCs}
          selectedLines={selectedLines}
          challans={challans}
          lines={lines}
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            setSelectedDCs([]);
            setSelectedLines([]);
            load();
            toast.success("Tally invoice uploaded and linked");
          }}
        />
      )}

      {showAdvance && (
        <AdvanceDialog
          projectCode={code}
          projectName={project.name}
          clientName={project.client_name}
          onClose={() => setShowAdvance(false)}
          onSuccess={() => {
            setShowAdvance(false);
            load();
            toast.success("Advance invoice raised");
          }}
        />
      )}

      {recordPayFor && (
        <RecordPaymentModal
          invoice={{ ...recordPayFor, upr_no: data?.project?.upr_no || "" }}
          onClose={() => setRecordPayFor(null)}
          onSaved={() => { setRecordPayFor(null); load(); toast.success("Payment recorded"); }}
        />
      )}
      {followupFor && (
        <FollowupModal
          invoice={followupFor}
          onClose={() => setFollowupFor(null)}
          onSaved={() => { setFollowupFor(null); load(); toast.success("Follow-up logged"); }}
        />
      )}
      {historyFor && (
        <FollowupHistoryModal
          invoice={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone, icon }) {
  const tones = {
    emerald: "text-emerald-300", amber: "text-amber-300", rose: "text-rose-300",
  };
  const tid = "kpi-" + String(label).toLowerCase().replace(/\s+/g, "-");
  return (
    <div data-testid={tid}
         className="bg-white/5 backdrop-blur-sm rounded-2xl p-3.5 border border-white/10 hover:border-white/20 transition-colors">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/60">
        {icon}{label}
      </div>
      <div className={`font-display text-2xl font-bold mt-1 ${tones[tone] || "text-white"}`}>{value}</div>
    </div>
  );
}

// Phase 34 r5 (Msg 760) — TeamStrip removed from Accounts UI per spec.
// (Project-team display lives on the Project Detail page instead.)

function PaymentPill({ status }) {
  const map = {
    pending:    ["bg-amber-50 text-amber-700 border-amber-200",   "Pending"],
    partial:    ["bg-blue-50 text-blue-700 border-blue-200",      "Partial"],
    paid:       ["bg-emerald-50 text-emerald-700 border-emerald-200", "Paid"],
    overdue:    ["bg-rose-50 text-rose-700 border-rose-200",      "Overdue"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100 text-zinc-700 border-zinc-200", status || "—"];
  return (
    <span className={`inline-flex items-center px-2.5 h-6 rounded-full text-[11px] font-medium border ${cls}`}>
      {label}
    </span>
  );
}

function LineCard({ line, selectedDCs, onToggleDC, selectedLines = [], onToggleLine, code }) {
  const photos = line.installation_photos || [];
  const po = line.linked_po;
  const outsourced = line.production_type === "outsourced";
  const billableNoDc = !!line.billable_without_dc && !line.is_invoiced;
  const lineSelected = selectedLines.includes(line.boq_id);
  return (
    <div className="card-flat p-5" data-testid={`line-card-${line.boq_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          {line.wall_no && (
            <div className="font-mono text-[11px] text-orange-600 font-medium">{line.wall_no}</div>
          )}
          <h3 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            {line.material_name}
            {outsourced && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-violet-50 text-violet-700 border border-violet-200">
                <ExternalLink className="w-2.5 h-2.5" /> Outsourced
              </span>
            )}
          </h3>
          <div className="text-xs text-zinc-500 mt-0.5">
            Ordered <b>{line.ordered_qty}</b> {line.unit}
            {outsourced ? (
              <> · {line.line_complete || line.production_file_status === "approved"
                ? <b className="text-emerald-700">production file approved · complete</b>
                : <span className="text-zinc-400">awaiting final production file approval</span>}</>
            ) : (
              <> · Delivered <b className="text-emerald-700">{line.delivered_qty}</b> ·
              {line.pending_qty > 0 ? (
                <> Pending <b className="text-amber-700">{line.pending_qty}</b></>
              ) : <> <b className="text-emerald-700">complete</b></>}</>
            )}
          </div>
          {/* PO indicator + Download PO button for Accounts */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {po ? (
              <>
                <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-mono uppercase tracking-widest border bg-emerald-50 text-emerald-700 border-emerald-200"
                      data-testid={`line-po-ok-${line.boq_id}`}>
                  <CheckCircle2 className="w-3 h-3" /> {po.doc_type === "email" ? "Email OK" : `PO ${po.po_number || "✓"}`}
                </span>
                <DownloadPOBtn code={code} poId={po.id} fileName={po.file_name} />
              </>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-mono uppercase tracking-widest border bg-rose-50 text-rose-700 border-rose-200"
                    data-testid={`line-po-missing-${line.boq_id}`}>
                <AlertTriangle className="w-3 h-3" /> PO / Email Confirmation Required
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {line.is_invoiced ? (
            <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="w-3 h-3" /> Invoiced
            </span>
          ) : outsourced && billableNoDc ? (
            <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium border bg-violet-50 text-violet-700 border-violet-200">
              <Receipt className="w-3 h-3" /> Ready to bill · no DC
            </span>
          ) : line.delivered_qty > 0 ? (
            <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium border bg-amber-50 text-amber-700 border-amber-200">
              <Receipt className="w-3 h-3" /> Ready to bill
            </span>
          ) : null}
        </div>
      </div>

      {/* Outsourced — bill directly, no DC */}
      {outsourced ? (
        line.is_invoiced ? (
          <div className="text-xs text-emerald-700 italic">Invoiced. No Delivery Challan applies to outsourced lines.</div>
        ) : billableNoDc ? (
          <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${lineSelected ? "border-violet-500 bg-violet-50 ring-2 ring-violet-100" : "border-zinc-200 hover:border-violet-300"}`}
                 data-testid={`bill-line-${line.boq_id}`}>
            <input type="checkbox" checked={lineSelected} onChange={() => onToggleLine(line.boq_id)} className="accent-violet-600 w-4 h-4" />
            <div className="flex-1">
              <div className="text-sm font-medium">Bill this line now</div>
              <div className="text-[11px] text-zinc-500">Outsourced — production file approved. No Delivery Challan needed.</div>
            </div>
            <Receipt className="w-4 h-4 text-violet-500" />
          </label>
        ) : (
          <div className="text-xs text-zinc-400 italic">Billing unlocks once the client approves the Final Production File.</div>
        )
      ) : (line.dcs || []).length === 0 ? (
        <div className="text-xs text-zinc-400 italic">No DCs raised yet for this line.</div>
      ) : (
        <div className="space-y-2">
          {line.dcs.map((d) => {
            const checked = selectedDCs.includes(d.challan_number);
            const billed = !!d.invoice_number;
            return (
              <div key={`${d.challan_number}-${d.created_at}`}
                   className={`p-2.5 border rounded-lg transition-colors
                     ${billed
                       ? "bg-zinc-50 border-zinc-200 opacity-90"
                       : checked
                         ? "bg-orange-50 border-orange-300"
                         : "bg-white border-zinc-200 hover:bg-zinc-50"}`}
                   data-testid={`dc-row-${d.challan_number}`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" disabled={billed} checked={checked}
                         onChange={() => onToggleDC(d.challan_number)}
                         data-testid={`dc-cbx-${d.challan_number}`}
                         className="w-4 h-4 accent-orange-600" />
                  <Truck className="w-4 h-4 text-zinc-400" />
                  <div className="flex-1">
                    <div className="text-sm font-mono text-zinc-800">{d.challan_number}</div>
                    <div className="text-[11px] text-zinc-500">
                      Qty {d.qty} {d.unit} · {fmtIST(d.created_at) || (d.created_at || "").slice(0, 10)}
                      {d.signed_file_url
                        ? <> · <span className="text-emerald-700 font-medium">signed</span></>
                        : <> · <span className="text-amber-700">unsigned</span></>}
                    </div>
                  </div>
                  <DCStatusPill status={d.status} />
                  {billed && (
                    <span className="text-[10px] font-mono text-zinc-500">
                      → {d.invoice_number}
                    </span>
                  )}
                </label>
                {/* Msg 875 — auth-aware view + download for the SIGNED DC PDF */}
                {d.signed_file_url && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap ml-7">
                    <SignedDCButtons cn={d.challan_number} fileName={d.signed_file_name} />
                    {d.signed_at && (
                      <span className="text-[10px] font-mono text-zinc-400">
                        Signed {fmtIST(d.signed_at)}{d.signed_by ? ` · ${d.signed_by}` : ""}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Installation photos for this line (real photos, not signed PDFs) */}
      {photos.length > 0 && (
        <details className="mt-3">
          <summary className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-zinc-800">
            <ImageIcon className="w-3 h-3 inline mr-1" />
            Installation photos ({photos.length})
          </summary>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2"
               data-testid={`install-photos-${line.boq_id}`}>
            {photos.map((p, i) => (
              <a key={`${p.url}-${i}`} href={p.url} target="_blank" rel="noopener noreferrer"
                 title={p.note || p.name || `Photo ${i + 1}`}
                 className="block aspect-square rounded-lg overflow-hidden bg-zinc-100 ring-1 ring-zinc-200 hover:ring-orange-400 transition">
                <img src={p.url} alt={p.name || p.cn || "install"}
                     className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/* Msg 875 — Bearer-auth download for per-line PO PDF.
   Msg 877 — Switched to server-streaming token URL so the browser pulls the
   file directly with a real Content-Disposition; bypasses every blob/MIME
   pitfall that produced a blank PDF. */
function DownloadPOBtn({ code, poId, fileName }) {
  const [busy, setBusy] = useState(false);
  async function dl() {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${code}/line-pos/${poId}/download-link`);
      if (!data?.url) throw new Error("no-url");
      // Direct navigation triggers the browser's normal download behaviour.
      // Hidden anchor keeps the page from navigating away.
      const a = document.createElement("a");
      a.href = data.url; a.download = fileName || `PO-${poId.slice(0, 8)}.pdf`;
      a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      const code = e.response?.status;
      toast.error(code === 403 ? "Not authorised to download PO"
                                : "PO download failed — try again");
    } finally {
      setBusy(false);
    }
  }
  return (
    <button onClick={dl} disabled={busy}
            data-testid={`download-po-${poId}`}
            className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-mono uppercase tracking-widest border bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-60">
      <Download className="w-3 h-3" /> {busy ? "…" : "Download PO"}
    </button>
  );
}

/* Msg 877 — Signed-DC viewer + downloader.
   Replaces the blob-fetch + window.open() approach (which sometimes produced
   a blank tab on 2nd / 3rd clicks due to popup-blocker timing) with a
   short-lived server-signed URL. The browser streams the PDF straight from
   the backend, exactly like clicking a normal link. View opens that URL in
   a new tab; Download triggers an <a download>. */
function SignedDCButtons({ cn, fileName }) {
  const [busy, setBusy] = useState(false);

  async function fetchLink() {
    const { data } = await api.post(`/challans/${cn}/signed-download-link`);
    if (!data?.url) throw new Error("no-url");
    return data.url;
  }

  async function view() {
    if (busy) return;
    setBusy(true);
    try {
      const url = await fetchLink();
      // Always streaming, never blob — every consecutive click works.
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Not authorised"
                                              : "Failed to open signed DC");
    } finally { setBusy(false); }
  }

  async function download() {
    if (busy) return;
    setBusy(true);
    try {
      const url = await fetchLink();
      const a = document.createElement("a");
      a.href = url; a.download = fileName || `${cn}-signed.pdf`;
      a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Not authorised"
                                              : "Failed to download signed DC");
    } finally { setBusy(false); }
  }

  return (
    <>
      <button onClick={view} disabled={busy}
              data-testid={`signed-dc-view-${cn}`}
              className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium border bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-60">
        <Eye className="w-3 h-3" /> {busy ? "…" : "View Signed DC"}
      </button>
      <button onClick={download} disabled={busy}
              data-testid={`signed-dc-download-${cn}`}
              className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-medium bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-60">
        <Download className="w-3 h-3" /> {busy ? "…" : "Download"}
      </button>
    </>
  );
}

function DCStatusPill({ status }) {
  const map = {
    issued:   ["bg-blue-50 text-blue-700 border-blue-200",       "Issued"],
    partial:  ["bg-amber-50 text-amber-700 border-amber-200",    "Partial"],
    delivered:["bg-teal-50 text-teal-700 border-teal-200",       "Delivered"],
    installed:["bg-emerald-50 text-emerald-700 border-emerald-200", "Installed"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100 text-zinc-600 border-zinc-200", status || "—"];
  return (
    <span className={`inline-flex items-center px-2 h-6 rounded-full text-[10px] font-mono border ${cls}`}>
      {label}
    </span>
  );
}

/* ----------------------------- Upload Modal ----------------------------- */
function UploadTallyModal({ projectCode, selectedDCs, selectedLines = [], challans, lines, onClose, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    invoice_number: "",
    invoice_date: today,
    invoice_value: "",
    tax_amount: "",
    due_date: "",
    remarks: "",
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  // boq_line_ids = DC-derived lines + directly-selected outsourced lines
  const linkedBoqIds = Array.from(new Set([
    ...selectedDCs.flatMap((cn) => {
      const ch = challans.find((c) => c.challan_number === cn);
      return (ch?.lines || []).map((ln) => ln.boq_id).filter(Boolean);
    }),
    ...selectedLines,
  ]));
  const linkedLines = lines.filter((l) => linkedBoqIds.includes(l.boq_id));
  const outsourcedSel = lines.filter((l) => selectedLines.includes(l.boq_id));

  async function submit() {
    if (!form.invoice_number.trim()) { toast.error("Enter Tally invoice number"); return; }
    if (!form.invoice_value) { toast.error("Enter invoice value"); return; }
    if (!file) { toast.error("Attach the Tally PDF"); return; }
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF accepted"); return; }
    setUploading(true);
    try {
      // 1) Upload PDF
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // 2) Create invoice link
      await api.post("/invoices/upload-tally", {
        project_code: projectCode,
        invoice_number: form.invoice_number.trim(),
        invoice_date: form.invoice_date,
        invoice_value: parseFloat(form.invoice_value),
        tax_amount: parseFloat(form.tax_amount || 0),
        due_date: form.due_date || null,
        file_url: up.data.url,
        file_name: up.data.filename,
        challan_numbers: selectedDCs,
        boq_line_ids: linkedBoqIds,
        remarks: form.remarks,
      });
      onSuccess();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  const total = (parseFloat(form.invoice_value || 0) + parseFloat(form.tax_amount || 0)) || 0;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         data-testid="upload-tally-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-600">Tally Invoice Upload</div>
            <h2 className="font-display text-xl font-bold tracking-tight mt-1">{projectCode}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {/* Selected DCs / outsourced lines preview */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              Linked DCs ({selectedDCs.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedDCs.map((cn) => (
                <span key={cn} className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-orange-50 text-orange-700 border border-orange-200 text-xs font-mono">
                  <Truck className="w-3 h-3" /> {cn}
                </span>
              ))}
              {selectedDCs.length === 0 && <span className="text-xs text-zinc-400">—</span>}
            </div>
            {outsourcedSel.length > 0 && (
              <>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-3 mb-2">
                  Outsourced lines — billed directly ({outsourcedSel.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {outsourcedSel.map((l) => (
                    <span key={l.boq_id} className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-violet-50 text-violet-700 border border-violet-200 text-xs">
                      <Receipt className="w-3 h-3" /> {l.material_name}
                    </span>
                  ))}
                </div>
              </>
            )}
            {linkedLines.length > 0 && (
              <div className="mt-2 text-[11px] text-zinc-500">
                Will be linked to {linkedLines.length} BOQ line{linkedLines.length > 1 && "s"}:&nbsp;
                {linkedLines.slice(0, 3).map((l) => l.material_name).join(", ")}
                {linkedLines.length > 3 && ` +${linkedLines.length - 3} more`}
              </div>
            )}
          </div>

          {/* Form */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Tally Invoice #" required>
              <input value={form.invoice_number}
                     onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                     placeholder="e.g. INV-2526-001"
                     data-testid="ti-invoice-number"
                     className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
            </Field>
            <Field label="Invoice Date" required>
              <input type="date" value={form.invoice_date}
                     onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                     data-testid="ti-invoice-date"
                     className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
            </Field>
            <Field label="Taxable Amount (₹)" required>
              <input type="number" step="0.01" value={form.invoice_value}
                     onChange={(e) => setForm({ ...form, invoice_value: e.target.value })}
                     placeholder="100000"
                     data-testid="ti-invoice-value"
                     className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
            </Field>
            <Field label="GST Amount (₹)">
              <input type="number" step="0.01" value={form.tax_amount}
                     onChange={(e) => setForm({ ...form, tax_amount: e.target.value })}
                     placeholder="18000"
                     data-testid="ti-tax-amount"
                     className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
            </Field>
            <Field label="Due Date">
              <input type="date" value={form.due_date}
                     onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                     data-testid="ti-due-date"
                     className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
            </Field>
            <Field label="Total">
              <div className="h-10 px-3 flex items-center bg-zinc-50 border border-zinc-200 rounded-lg text-sm font-mono">
                <IndianRupee className="w-3.5 h-3.5 mr-1 text-zinc-400" />
                {total.toLocaleString("en-IN")}
              </div>
            </Field>
          </div>

          <Field label="Remarks">
            <textarea value={form.remarks} rows={2}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      data-testid="ti-remarks"
                      className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
          </Field>

          {/* PDF picker */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              Tally PDF <span className="text-rose-600">*</span>
            </div>
            <div className="border-2 border-dashed border-zinc-300 rounded-xl p-5 text-center bg-zinc-50/40">
              <input ref={fileInput} type="file" accept="application/pdf,.pdf" hidden
                     data-testid="ti-pdf-input"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-rose-600" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-zinc-400">({(file.size / 1024).toFixed(0)} KB)</span>
                  <button onClick={() => setFile(null)}
                          className="ml-2 text-rose-600 hover:underline text-xs">remove</button>
                </div>
              ) : (
                <button type="button" onClick={() => fileInput.current?.click()}
                        className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800">
                  <Upload className="w-4 h-4" /> Choose Tally PDF
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose}
                  className="px-4 h-10 rounded-full border border-zinc-300 text-sm font-medium hover:bg-zinc-50">
            Cancel
          </button>
          <button onClick={submit} disabled={uploading}
                  data-testid="ti-submit-btn"
                  className="px-5 h-10 rounded-full bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-60">
            {uploading ? "Uploading…" : "Upload & Link"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">
        {label} {required && <span className="text-rose-600">*</span>}
      </div>
      {children}
    </div>
  );
}


/* ----------------------------- Record Payment Modal ----------------------------- */
function RecordPaymentModal({ invoice, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const balance = invoice.balance_due ?? Math.max((invoice.total || 0) - (invoice.amount_received || 0), 0);
  const [form, setForm] = useState({
    amount: balance.toString(),
    payment_date: today,
    mode: "bank",
    reference: "",
    tds: "",
    remarks: "",
  });
  const [busy, setBusy] = useState(false);

  async function submit() {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try {
      await api.post("/payments", {
        invoice_id: invoice.id,
        amount: amt,
        payment_date: form.payment_date,
        mode: form.mode,
        reference: form.reference,
        tds: parseFloat(form.tds || 0),
        remarks: form.remarks,
      });
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Could not record payment");
    } finally { setBusy(false); }
  }

  return (
    <ModalShell title="Record Payment" subtitle={`${invoice.invoice_number}${invoice.upr_no ? ` · UPR ${invoice.upr_no}` : ""}`} onClose={onClose}
                testId="record-payment-modal">
      <div className="space-y-4">
        {invoice.upr_no && (
          <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-mono font-semibold" data-testid="rp-upr-chip">
            UPR {invoice.upr_no}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 p-3 rounded-xl bg-emerald-50/40 border border-emerald-100">
          <Stat label="Total"    value={`₹${(invoice.total || 0).toLocaleString("en-IN")}`} />
          <Stat label="Received" value={`₹${(invoice.amount_received || 0).toLocaleString("en-IN")}`} tone="emerald" />
          <Stat label="Pending"  value={`₹${balance.toLocaleString("en-IN")}`} tone="rose" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Amount Received (₹)" required>
            <input type="number" step="0.01" value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   data-testid="rp-amount"
                   className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
          </Field>
          <Field label="Payment Date" required>
            <input type="date" value={form.payment_date}
                   onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                   data-testid="rp-date"
                   className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
          </Field>
          <Field label="Mode">
            <select value={form.mode}
                    onChange={(e) => setForm({ ...form, mode: e.target.value })}
                    data-testid="rp-mode"
                    className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm">
              <option value="bank">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
            </select>
          </Field>
          <Field label="Reference / UTR / Cheque #">
            <input value={form.reference}
                   onChange={(e) => setForm({ ...form, reference: e.target.value })}
                   data-testid="rp-reference"
                   className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
          </Field>
          <Field label="TDS Deducted (₹)">
            <input type="number" step="0.01" value={form.tds}
                   onChange={(e) => setForm({ ...form, tds: e.target.value })}
                   data-testid="rp-tds"
                   className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
          </Field>
        </div>
        <Field label="Remarks">
          <textarea rows={2} value={form.remarks}
                    onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                    data-testid="rp-remarks"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose}
                  className="px-4 h-10 rounded-full border border-zinc-300 text-sm font-medium hover:bg-zinc-50">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="rp-submit"
                  className="px-5 h-10 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">
            {busy ? "Saving…" : "Record Payment"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ----------------------------- Follow-up Modal ----------------------------- */
function FollowupModal({ invoice, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!note.trim()) { toast.error("Write a follow-up note first"); return; }
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/followup`, {
        note: note.trim(),
        next_followup_date: next || null,
      });
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Could not log follow-up");
    } finally { setBusy(false); }
  }

  return (
    <ModalShell title="Add Follow-up" subtitle={invoice.invoice_number} onClose={onClose}
                testId="followup-modal">
      <div className="space-y-4">
        <Field label="Note (what was said?)" required>
          <textarea rows={4} value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="fu-note"
                    placeholder="e.g. Called accounts contact — payment promised by 28th."
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
        </Field>
        <Field label="Next follow-up date (optional)">
          <input type="date" value={next}
                 onChange={(e) => setNext(e.target.value)}
                 data-testid="fu-next"
                 className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose}
                  className="px-4 h-10 rounded-full border border-zinc-300 text-sm font-medium hover:bg-zinc-50">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="fu-submit"
                  className="px-5 h-10 rounded-full bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-60">
            {busy ? "Saving…" : "Log Follow-up"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ---------- Follow-up history (read) ---------- */
function FollowupHistoryModal({ invoice, onClose }) {
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get(`/invoices/${invoice.id}/followups`)
       .then((r) => setList(r.data?.followups || []))
       .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Load failed"); setList([]); });
  }, [invoice.id]);
  return (
    <ModalShell title="Follow-up Trail" subtitle={invoice.invoice_number} onClose={onClose}
                testId="followup-history-modal">
      {list === null ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-zinc-400 italic">No follow-ups logged yet.</div>
      ) : (
        <ul className="space-y-3">
          {[...list].reverse().map((f) => (
            <li key={f.id} className="rounded-xl border border-zinc-200 bg-white p-3"
                data-testid={`fu-row-${f.id}`}>
              <div className="text-[11px] text-zinc-500 font-mono mb-1">
                {fmtIST(f.at)} · {f.by}
                {f.next_followup_date && (
                  <span className="ml-2 inline-flex items-center gap-1 text-orange-700">
                    · next: {f.next_followup_date}
                  </span>
                )}
              </div>
              <div className="text-sm text-zinc-800 whitespace-pre-wrap">{f.note}</div>
            </li>
          ))}
        </ul>
      )}
    </ModalShell>
  );
}

function ModalShell({ title, subtitle, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         data-testid={testId} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-600">{title}</div>
            <h2 className="font-display text-lg font-bold tracking-tight mt-0.5">{subtitle}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = { emerald: "text-emerald-700", rose: "text-rose-700" };
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-bold text-lg ${tones[tone] || "text-zinc-900"}`}>{value}</div>
    </div>
  );
}


/* ─────────────────────── Advance Invoice card ───────────────────────
   One glance: status (raised / pending) + total + balance + CTA.
   Sits at the top of the Accounts Project page so the team is reminded
   to collect the upfront before production starts. */
function AdvanceCard({ existing, onRaise }) {
  if (existing) {
    const total = existing.total || existing.invoice_value || 0;
    const recv = existing.amount_received || 0;
    const bal = existing.balance_due ?? Math.max(total - recv, 0);
    const fullyPaid = bal === 0 && recv > 0;
    return (
      <div className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 p-5"
           data-testid="advance-card-existing">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-full bg-amber-500/15 text-amber-700 flex items-center justify-center flex-shrink-0">
            <IndianRupee className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-display font-bold text-base text-zinc-900">Advance Invoice</h3>
              <span className="font-mono text-[11px] text-zinc-600">{existing.invoice_number}</span>
              <span className={`px-2 h-5 inline-flex items-center rounded-full text-[10px] font-mono uppercase tracking-widest ${
                fullyPaid ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-200/70 text-amber-900"
              }`}>
                {fullyPaid ? "Paid" : "Awaiting payment"}
              </span>
            </div>
            <div className="text-xs text-zinc-600 flex flex-wrap gap-x-5 gap-y-1">
              <span>Total: <strong className="text-zinc-900 font-mono">₹{total.toLocaleString("en-IN")}</strong></span>
              <span>Received: <strong className="text-emerald-700 font-mono">₹{recv.toLocaleString("en-IN")}</strong></span>
              <span>Balance: <strong className="text-rose-700 font-mono">₹{bal.toLocaleString("en-IN")}</strong></span>
              {existing.invoice_date && (
                <span>Raised: <span className="font-mono">{fmtIST(existing.invoice_date) || existing.invoice_date.slice(0, 10)}</span></span>
              )}
            </div>
            {existing.remarks && (
              <div className="mt-1 text-[11px] italic text-zinc-500">"{existing.remarks}"</div>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="mb-6 rounded-2xl border border-dashed border-amber-300 bg-amber-50/50 p-5"
         data-testid="advance-card-empty">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-11 h-11 rounded-full bg-amber-500/15 text-amber-700 flex items-center justify-center flex-shrink-0">
          <IndianRupee className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-base text-zinc-900">Raise an Advance Invoice</h3>
          <p className="text-xs text-zinc-600 mt-0.5">
            Collect the upfront before production starts. One invoice per project.
          </p>
        </div>
        <button onClick={onRaise} data-testid="raise-advance-btn"
                className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition">
          <IndianRupee className="w-4 h-4" /> Raise Advance
        </button>
      </div>
    </div>
  );
}

/* ───────────────────── Advance Invoice dialog ──────────────────────
   Minimal 4-field form (number, date, value, remarks) + optional PDF.
   Backend computes total/balance from value + tax. No tax field shown by
   default — Accounts can edit the invoice later if needed. */
function AdvanceDialog({ projectCode, projectName, clientName, onClose, onSuccess }) {
  const [form, setForm] = useState({
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    invoice_value: "",
    tax_amount: "",
    remarks: "Advance against project",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const value = parseFloat(form.invoice_value) || 0;
  const tax = parseFloat(form.tax_amount) || 0;
  const total = value + tax;

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    if (!form.invoice_number.trim()) return toast.error("Invoice number required");
    if (value <= 0) return toast.error("Invoice value must be greater than 0");
    setBusy(true);
    try {
      // 1) Optional PDF upload first
      let fileUrl = "", fileName = "";
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const up = await api.post("/upload", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        fileUrl = up.data.url; fileName = up.data.filename;
      }
      // 2) Create the invoice
      await api.post("/invoices", {
        project_code: projectCode,
        invoice_number: form.invoice_number.trim(),
        invoice_date: form.invoice_date,
        milestone: "advance",
        invoice_value: value,
        tax_amount: tax,
        total,
        challan_numbers: [],
        boq_line_ids: [],
        remarks: form.remarks,
        file_url: fileUrl, file_name: fileName,
      });
      onSuccess();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed to raise advance");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} data-testid="advance-form"
            className="bg-white max-w-lg w-full max-h-[90vh] flex flex-col rounded-2xl shadow-xl border border-zinc-200 overflow-hidden">
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <div>
            <h3 className="font-display font-bold text-lg">Raise Advance Invoice</h3>
            <div className="text-[11px] font-mono text-zinc-500 mt-0.5">{projectCode} · {clientName}</div>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Invoice # <span className="text-rose-600">*</span></label>
              <input value={form.invoice_number}
                     onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                     placeholder="e.g. INV-2026-ADV-001"
                     required
                     data-testid="adv-number"
                     className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Date</label>
              <input type="date" value={form.invoice_date}
                     onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                     data-testid="adv-date"
                     className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Invoice value (₹) <span className="text-rose-600">*</span></label>
              <input type="number" min="0" step="0.01"
                     value={form.invoice_value}
                     onChange={(e) => setForm({ ...form, invoice_value: e.target.value })}
                     placeholder="0.00" required
                     data-testid="adv-value"
                     className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-mono text-right" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Tax / GST (₹)</label>
              <input type="number" min="0" step="0.01"
                     value={form.tax_amount}
                     onChange={(e) => setForm({ ...form, tax_amount: e.target.value })}
                     placeholder="0.00"
                     data-testid="adv-tax"
                     className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-mono text-right" />
            </div>
          </div>

          {/* Live total preview */}
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-amber-900">Total advance to be invoiced</span>
            <span className="font-display font-bold text-xl text-amber-900 font-mono">
              ₹{total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </span>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Remarks</label>
            <textarea value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      rows={2} data-testid="adv-remarks"
                      className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Attach Tally / advance invoice PDF (optional)</label>
            <input ref={fileRef} type="file" accept="application/pdf,image/*"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   data-testid="adv-file"
                   className="hidden" />
            <button type="button" onClick={() => fileRef.current?.click()}
                    className="w-full px-3 h-10 rounded-lg border border-dashed border-zinc-300 text-sm text-zinc-600 hover:bg-zinc-50 inline-flex items-center justify-center gap-2">
              <Upload className="w-4 h-4" />
              {file ? file.name : "Choose PDF / image…"}
            </button>
            {file && (
              <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                      className="mt-1 text-[11px] text-zinc-400 hover:text-zinc-700">remove</button>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 flex gap-2 px-5 py-3 bg-zinc-50 border-t border-zinc-200">
          <button type="submit" disabled={busy} data-testid="adv-submit"
                  className="flex-1 h-11 rounded-xl bg-amber-600 text-white font-medium hover:bg-amber-700 disabled:opacity-60">
            {busy ? "Raising…" : "Raise advance"}
          </button>
          <button type="button" onClick={onClose}
                  className="h-11 px-4 rounded-xl border border-zinc-300 text-zinc-700 font-medium hover:bg-white">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
